import React from 'react'
import { AlertTriangle, X, Package } from 'lucide-react'
import { StockAlert } from '@/types/pos'

interface StockAlertBannerProps {
  alerts: StockAlert[]
  onDismiss?: (productId: string) => void
  onDismissAll?: () => void
}

export const StockAlertBanner: React.FC<StockAlertBannerProps> = ({
  alerts,
  // onDismiss - reserved for future per-item dismiss
  onDismissAll,
}) => {
  if (alerts.length === 0) return null

  const criticalAlerts = alerts.filter(a => a.severity === 'critical')
  const warningAlerts = alerts.filter(a => a.severity === 'warning')

  return (
    <div className="mb-4 space-y-2">
      {/* Critical Alerts (Out of Stock) */}
      {criticalAlerts.length > 0 && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-1.5 bg-red-500/20 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-red-500" />
              </div>
              <div>
                <p className="font-semibold text-red-500 text-sm">
                  {criticalAlerts.length} Item{criticalAlerts.length > 1 ? 's' : ''} Out of Stock
                </p>
                <div className="mt-1 space-y-1">
                  {criticalAlerts.slice(0, 3).map(alert => (
                    <p key={alert.productId} className="text-xs text-red-400 flex items-center gap-2">
                      <Package className="w-3 h-3" />
                      <span className="font-medium">{alert.productName}</span>
                      <span className="text-red-300">({alert.currentStock} left)</span>
                    </p>
                  ))}
                  {criticalAlerts.length > 3 && (
                    <p className="text-xs text-red-300">
                      +{criticalAlerts.length - 3} more items
                    </p>
                  )}
                </div>
              </div>
            </div>
            {onDismissAll && (
              <button
                onClick={onDismissAll}
                className="p-1 hover:bg-red-500/20 rounded transition-colors"
              >
                <X className="w-4 h-4 text-red-400" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Warning Alerts (Low Stock) */}
      {warningAlerts.length > 0 && (
        <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-1.5 bg-yellow-500/20 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-yellow-500" />
              </div>
              <div>
                <p className="font-semibold text-yellow-600 dark:text-yellow-400 text-sm">
                  {warningAlerts.length} Item{warningAlerts.length > 1 ? 's' : ''} Running Low
                </p>
                <div className="mt-1 space-y-1">
                  {warningAlerts.slice(0, 3).map(alert => (
                    <p key={alert.productId} className="text-xs text-yellow-600 dark:text-yellow-300 flex items-center gap-2">
                      <Package className="w-3 h-3" />
                      <span className="font-medium">{alert.productName}</span>
                      <span className="opacity-80">({alert.currentStock}/{alert.minStock} min)</span>
                    </p>
                  ))}
                  {warningAlerts.length > 3 && (
                    <p className="text-xs text-yellow-500">
                      +{warningAlerts.length - 3} more items
                    </p>
                  )}
                </div>
              </div>
            </div>
            {onDismissAll && warningAlerts.length > 0 && criticalAlerts.length === 0 && (
              <button
                onClick={onDismissAll}
                className="p-1 hover:bg-yellow-500/20 rounded transition-colors"
              >
                <X className="w-4 h-4 text-yellow-400" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// Inline Stock Warning for individual products in cart
export const InlineStockWarning: React.FC<{
  currentStock: number
  minStock: number
  quantityInCart: number
}> = ({ currentStock, minStock, quantityInCart }) => {
  const remainingAfterSale = currentStock - quantityInCart
  
  if (remainingAfterSale < 0) {
    return (
      <span className="text-[10px] text-red-500 font-semibold flex items-center gap-1">
        <AlertTriangle className="w-3 h-3" />
        Insufficient stock!
      </span>
    )
  }
  
  if (remainingAfterSale <= minStock) {
    return (
      <span className="text-[10px] text-yellow-500 flex items-center gap-1">
        <AlertTriangle className="w-3 h-3" />
        Low stock after sale
      </span>
    )
  }

  return null
}

export default StockAlertBanner
